// ===========================================================================
// LAS TRES CERTIFICACIONES. Datos tomados de zenkai.com.mx, no de maquetas.
//
// El modelo que gobierna este archivo:
//
//   · La CATEGORÍA es la casa temática de un curso. Cada curso tiene una, y
//     solo una: 5S es de Lean, ANOVA es de Six Sigma.
//   · La CERTIFICACIÓN es una ruta curada que toma cursos de donde haga falta.
//
// Por eso los módulos llevan `cat`: en Validación de Procesos, MSA, Pruebas de
// Hipótesis y ANOVA son de la categoría SIX SIGMA aunque formen parte de esta
// certificación —quien valida procesos necesita esa estadística. Mostrarlo es
// el punto, no un detalle: explica por qué un ingeniero de calidad termina
// estudiando análisis de varianza.
//
// Las cuatro preguntas frecuentes son las mismas en las tres páginas del sitio
// actual, así que viven una sola vez aquí abajo.
// ===========================================================================

import { cursoPorNombre } from './cursos.js';

const FAQ = [
  ['¿Qué beneficio tiene certificarse con Zenkai?',
   'Puedes dominar y certificarte como en cualquier otra institución, con la diferencia de que nuestros cursos y certificaciones están hechos con los mejores estándares de calidad. Encima, tienes todos los cursos y certificaciones bajo una misma suscripción, sin límites.'],
  ['¿Qué soporte ofrecen durante la formación?',
   'Un soporte integral durante toda la formación, con acceso a tutores expertos que responden preguntas técnicas y guían en el desarrollo de los proyectos. Además, un equipo de atención al cliente disponible para resolver cualquier inconveniente con la plataforma o el acceso a los cursos.'],
  ['¿Ofrecen formación en varios idiomas?',
   'Actualmente nuestros cursos y certificaciones están en español. Estamos trabajando para que estén disponibles también en inglés y portugués, cuidando que tanto el contenido teórico como el práctico se traduzcan adecuadamente.'],
  ['¿Cómo se actualizan los cursos y contenidos?',
   'Se actualizan periódicamente para garantizar que estén alineados con las últimas tendencias y herramientas del sector. Mantenemos una estrecha relación con la industria para integrar las mejores prácticas y asegurar que las certificaciones tengan un valor vigente y aplicable.'],
];

const INCLUYE = [
  'Certificado profesional digital',
  'Diploma digital',
  'Formatos editables descargables',
  'Webinar mensual y asesoría grupal mensual',
];

// Las categorías, con el color que ya usa el sistema para cada bloque.
// "Validación de Procesos" NO es una categoría: es la certificación que sale de
// Ingeniería de Calidad. Tenerla como categoría además de certificación era
// repetir el error del sitio viejo —dos cosas distintas con el mismo nombre— y
// dejaba una categoría de un solo curso. La validación vive dentro de calidad,
// que es además donde el propio sitio actual publica TMV y Protocolos y
// Reportes de Validación.
// `ruta` vive aquí y no en cada página: la tenían por su cuenta la ficha de
// curso, el megamenú y Especialízate, y basta con que una se quede atrás para
// que un enlace apunte a una URL que ya no existe. Ya pasó al renombrar
// /validacion-de-procesos.
export const CATEGORIAS = {
  lean: { nombre: 'Lean Manufacturing', tono: 'e', ruta: '/lean-manufacturing' },
  // Julio pidió que la categoría se llame como se vende: "Six Sigma Green Belt".
  // Queda con el MISMO nombre que su certificación —lo señalé y lo confirmó—.
  // Ojo al leer el sitio: la categoría son 13 cursos (Minitab incluido) y la
  // certificación son 12; que compartan nombre no las vuelve lo mismo.
  six: { nombre: 'Six Sigma Green Belt', tono: 'pn', ruta: '/six-sigma' },
  // "Process Validation" y no "Ingeniería de Calidad": decisión del usuario en
  // septiembre de 2026, para que las tres categorías se llamen igual que las
  // tres certificaciones —como ya se hizo con Six Sigma Green Belt—.
  //
  // SE ADVIRTIÓ Y SE CONFIRMÓ: la categoría agrupa 15 cursos y solo 8 están en
  // la ruta de validación. Buenas Prácticas de Manufactura, Lectura de Planos y
  // Cuartos Limpios quedan bajo una etiqueta que no los describe.
  //
  // La RUTA sigue siendo /ingenieria-de-calidad a propósito: cambiarla rompería
  // los enlaces que ya existen y lo que Google tenga indexado. Se cambia el
  // nombre que se ve, no la dirección.
  cal: { nombre: 'Process Validation', tono: 'hs', ruta: '/ingenieria-de-calidad' },
};

export const CERTIFICACIONES = [
  {
    slug: 'lean-leader',
    cat: 'lean',          // su casa temática: contra esta se marcan los módulos ajenos
    nombre: 'Lean Leader',
    kicker: 'Certificación · Lean Manufacturing',
    titulo: 'Lean Leader',
    entrada: 'Liderazgo estratégico en Lean. Once cursos que van del <b>pensamiento Lean</b> hasta el kaizen en piso y en oficina —para quien va a dirigir la mejora, no solo a ejecutarla.',
    horas: '40 h',
    logrosTitulo: 'De la estrategia en el papel a la rutina en el piso.',
    logros: [
      'Leer la operación completa con Lean Thinking y llevarla al piso',
      'Mapear la cadena de valor y encontrar dónde se pierde el tiempo',
      'Sostener la mejora con una rutina de gestión diaria',
      'Reducir tiempos de cambio con SMED y el Set Up Wheel',
      'Implementar un sistema de jalón y flujo continuo',
      'Facilitar un evento kaizen, en piso y en procesos administrativos',
    ],
    // El cuarto elemento son los minutos de ejercicio. Aquí son ESTIMADOS
    // —ver `ejerciciosEstimados` más abajo—: pesan donde el curso se practica
    // haciendo (mapear tu cadena de valor, calcular tu kanban) y se concentran
    // en los dos kaizen, que es donde se ejecuta el proyecto en planta.
    modulos: [
      // ONCE, EN ESTE ORDEN — el de la plataforma, confirmado por el usuario en
      // septiembre de 2026. Cambió respecto a la lista anterior: SALIÓ Hoshin
      // Kanri (sigue existiendo como curso suelto en Lean Manufacturing, solo
      // que ya no forma parte de la ruta) y Quality Inspection Kaizen se movió
      // del sexto lugar al último.
      ['Lean Thinking', 'Fundamentos del pensamiento Lean', 'lean', 30],
      ['Value Stream Mapping', 'Mapeo de la cadena de valor', 'lean', 90],
      ['Lean Daily Management', 'Gestión diaria para líderes Lean', 'lean', 60],
      ['Set Up Wheel', 'Estandarización de cambios rápidos', 'lean', 45],
      ['Pull System', 'Implementación del sistema de jalón Lean', 'lean', 60],
      ['Líder de Evento Kaizen', 'Cómo guiar un kaizen exitoso', 'lean', 240],
      ['5S', 'Orden y disciplina en el entorno de trabajo', 'lean', 45],
      ['SMED', 'Reducción de tiempos de cambio rápido', 'lean', 45],
      ['Continuous Flow', 'Flujo continuo en procesos Lean', 'lean', 60],
      ['Administrative Process Kaizen', 'Kaizen en procesos administrativos', 'lean', 180],
      ['Quality Inspection Kaizen', 'Mejora en procesos de calidad', 'lean', 45],
    ],
    // Los 960 min de ejercicio son un ESTIMADO nuestro, no un dato del plan de
    // estudios: su columna de ejercicios viene vacía. Se repartieron para que
    // cuadren con las 40 h que la certificación ya anuncia. Por eso la página
    // los publica como aproximados ("unas 16 h", "alrededor de 40 h") en vez de
    // fingir la precisión que sí tiene Validación de Procesos.
    ejerciciosEstimados: true,
    formatos: [
      ['KPI Lean Daily Management', 'El tablero de la rutina diaria'],
      ['Matriz X · Hoshin Kanri', 'El despliegue de la estrategia al piso'],
      ['Lean Daily Management Meetings', 'La estructura de la junta de arranque'],
    ],
    paraQuien: 'Directores, Gerentes, Supervisores, Ingenieros de Proyectos, Superintendentes y Líderes de equipos Lean que buscan implementar Lean Leader Standard Work, fortalecer sus habilidades de gestión y evolucionar hacia un rol estratégico.',
    instructores: [
      ['Julio Rodríguez Pasos', 'Consultor Lean Enterprise · +25 años', '/julio-pasos.webp',
       'Los primeros 17 años apenas le alcanzaron para una gerencia chica. Cuando entendió lo que faltaba, en 6 años llegó a Director Global en una empresa de clase mundial —y fundó Zenkai para que a ti te tome una fracción de eso.'],
    ],
    hilo: {
      clase: 'Pull System',
      pregunta: 'Mi demanda varía 30% entre semanas. ¿Calculo el kanban con el promedio o con el pico?',
      quien: 'Ricardo M.',
      instructor: 'Julio Pasos',
      iniciales: 'JP',
      respuesta: 'Con el pico, pero acota el horizonte a 4 semanas. Si usas el promedio te quedas corto justo cuando más duele. Sube tus datos a la asesoría del mes y lo calculamos juntos.',
    },
  },

  {
    slug: 'six-sigma-green-belt',
    cat: 'six',          // su casa temática: contra esta se marcan los módulos ajenos
    nombre: 'Six Sigma Green Belt',
    kicker: 'Certificación · Six Sigma',
    titulo: 'Six Sigma Green Belt',
    entrada: 'Cuando el problema es la variación, la intuición no alcanza. Doce cursos que llevan de la <b>estadística descriptiva</b> al control estadístico de proceso, para que tus conclusiones se sostengan con datos.',
    horas: '40 h',
    logrosTitulo: 'La estadística deja de ser teoría y cierra el proyecto.',
    logros: [
      'Definir el problema y levantar la situación actual con datos',
      'Describir un proceso con estadística descriptiva y su distribución',
      'Validar que tu sistema de medición sea confiable con MSA',
      'Comparar escenarios con pruebas de hipótesis y ANOVA',
      'Modelar relaciones entre variables con regresiones simples y múltiples',
      'Sostener la mejora con control estadístico de proceso',
    ],
    // El cuarto elemento son los minutos de ejercicio. Cuatro de ellos NO son
    // estimados: 45, 60, 150 y 105 son los que el plan de Validación de Procesos
    // le asigna a esos mismos cursos, y se reusan tal cual. El resto sí se
    // estimó, incluidos los 570 min del proyecto DMAIC que se ejecuta al cerrar
    // con control estadístico —de ahí que el último módulo cargue 660—.
    modulos: [
      ['Agenda e Introducción al Curso', 'Cómo se recorre la certificación', 'six', 0],
      ['Introducción a Six Sigma Green Belt', 'Qué resuelve la metodología y cuándo aplica', 'six', 0],
      ['Definición de Problemas y Situación Actual', 'El planteamiento que decide el proyecto', 'six', 45],
      ['Estadística Descriptiva', 'Describir un proceso antes de juzgarlo', 'six', 60],
      ['Distribución de la Probabilidad', 'Cómo se comporta la variación', 'six', 45],
      ['Análisis de los Sistemas de Medición', 'MSA: confiar en el dato antes de decidir', 'six', 60],
      ['Estudios de Concordancia', 'Cuando el criterio es humano y no un instrumento', 'six', 45],
      ['Análisis de Datos Continuos', 'Qué dicen los datos que sí se miden', 'six', 60],
      ['Pruebas de Hipótesis', 'Probar la diferencia en vez de suponerla', 'six', 150],
      ['ANOVA', 'Comparar más de dos escenarios a la vez', 'six', 105],
      ['Regresiones (Simples y Múltiples)', 'Modelar la relación entre variables', 'six', 90],
      ['Control Estadístico de Proceso', 'Sostener la mejora en el tiempo', 'six', 660],
    ],
    // Ver la nota de Lean Leader: el total de ejercicios es un estimado nuestro
    // y la página lo publica como aproximado.
    ejerciciosEstimados: true,
    formatos: [
      ['Ejercicios de Estadística Descriptiva', 'Para practicar con tus propios datos'],
      ['Ejercicios de MSA', 'El estudio de repetibilidad y reproducibilidad'],
      ['Ejercicios de ANOVA', 'Comparación de escenarios paso a paso'],
    ],
    paraQuien: 'Ingenieros de Calidad, Supervisores de Producción, Analistas de Datos, Líderes de Mejora Continua, Profesionales de Operaciones y cualquier persona interesada en obtener la certificación Six Sigma Green Belt para implementar estrategias de optimización de procesos, reducción de costos y mejora continua dentro de sus organizaciones.',
    instructores: [
      ['Carlos Zamora', 'Consultor en Six Sigma', '/carlos-zamora.webp',
       'Consultor en activo. Las clases salen de proyectos que él mismo cerró en planta, no de un libro de texto.'],
    ],
    hilo: {
      clase: 'Pruebas de Hipótesis',
      pregunta: 'Mi p-value dio 0.06. ¿Eso significa que no hay diferencia entre los dos turnos?',
      quien: 'Daniela G.',
      instructor: 'Carlos Zamora',
      iniciales: 'CZ',
      respuesta: 'Significa que con esa muestra no la puedes demostrar, que no es lo mismo. Revisa el tamaño de muestra antes de concluir: con n pequeña, una diferencia real se te escapa. Tráelo a la asesoría y lo vemos con tus datos.',
    },
  },

  {
    slug: 'validacion-de-procesos',
    cat: 'cal',          // su casa temática: contra esta se marcan los módulos ajenos
    nombre: 'Process Validation',
    kicker: 'Certificación · Ingeniería de Calidad',
    titulo: 'Process Validation',
    entrada: 'Conviértete en el experto que garantiza calidad y eficiencia en la manufactura. Ocho cursos de <b>IQ, OQ y PQ</b> en ambiente regulado, del análisis de riesgo al mantenimiento del estado de validación.',
    horas: '26 h',
    logrosTitulo: 'La validación que se sostiene ante una auditoría.',
    logros: [
      'Ubicar la validación dentro de un ambiente regulado',
      'Evaluar el riesgo antes de definir el alcance de la validación',
      'Ejecutar las etapas de validación, de la pre validación al PQ',
      'Validar el sistema de medición y el método de prueba (MSA y TMV)',
      'Sustentar tus conclusiones con Minitab y pruebas estadísticas',
      'Mantener el estado de validación en el tiempo',
    ],
    // ESTOS DOCE, EN ESTE ORDEN. Confirmado por el usuario en agosto de 2026,
    // contra la hoja "Sumario Actual" del plan de estudios. Se llegó a plantear
    // que la certificación absorbiera los diez cursos de Ingeniería de Calidad
    // completos: NO es así. Solo toma dos —pFMEA (citado como "Riesgo Dentro
    // del Proceso de Validación") y TMV—, más la estadística.
    //
    // Cinco de los doce módulos vienen de la categoría Six Sigma: es la
    // estadística que la validación exige. Se marcan en la página.
    //
    // El CUARTO elemento son los minutos de EJERCICIO que el plan de estudios
    // asigna a ese módulo dentro de esta certificación. Suman 1320 min (22 h)
    // que, con los 1124 de video, dan las 40 h que se anuncian. El módulo de
    // "Manteniendo el Estado de Validación" carga 960 de ellos porque ahí se
    // ejecuta un proyecto de validación completo, de principio a fin.
    modulos: [
      // OCHO, EN ESTE ORDEN — el de la plataforma, confirmado por el usuario en
      // septiembre de 2026. Sustituye a la lista de DOCE que se había tomado en
      // agosto de la hoja "Sumario Actual" del plan de estudios. Las dos son
      // reales pero describen cosas distintas: aquella era el plan, esta es lo
      // que la plataforma sirve hoy. Salieron los cuatro módulos de estadística
      // prestados de Six Sigma —MSA, Distribución de la Probabilidad, Pruebas
      // de Hipótesis y ANOVA—, así que la ruta ya NO cruza categorías: Minitab
      // es el único módulo que viene de fuera.
      ['Herramientas Estadísticas en Validaciones (Minitab)', 'La herramienta con la que se sustenta', 'six', 0],
      ['Test Method Validation (TMV)', 'Validar el método con el que mides', 'cal', 0],
      ['Riesgo Dentro del Proceso de Validación', 'Qué validar y hasta dónde', 'cal', 0],
      ['Lean Dentro del Proceso de Validación', 'Validar sin cargarle desperdicio al proceso', 'cal', 0],
      ['Introducción y Validación de Procesos en un Ambiente Regulado', 'Las reglas del juego en industria regulada', 'cal', 0],
      ['Pre Validaciones de Procesos', 'Lo que se hace antes del protocolo', 'cal', 0],
      ['Etapas de Validaciones de Procesos', 'IQ, OQ y PQ, una por una', 'cal', 0],
      ['Manteniendo el Estado de Validación', 'Que no se caiga con el primer cambio', 'cal', 960],
    ],
    formatos: [
      ['Documento de Referencia · IQ Protocol', 'La instalación, documentada'],
      ['Documento de Referencia · PQ Protocol', 'El desempeño, demostrado'],
    ],
    paraQuien: 'Ingenieros de Calidad, Ingenieros de Validación, Supervisores de Producción, Especialistas en Cumplimiento Regulatorio, Líderes de Procesos, y profesionales de manufactura que buscan garantizar la confiabilidad, eficiencia y cumplimiento normativo en la validación de procesos industriales.',
    instructores: [
      ['Johan Rojas', 'Consultor en Ingeniería de Calidad y Validación de Procesos', '/johan-rojas.webp',
       'Consultor en activo en industria regulada. Trae a clase los hallazgos de auditoría que ha tenido que defender.'],
      ['Carlos Zamora', 'Consultor en Six Sigma', '/carlos-zamora.webp',
       'Cubre la parte estadística de la certificación: MSA, distribuciones, pruebas de hipótesis y ANOVA.'],
    ],
    hilo: {
      clase: 'Etapas de Validaciones de Procesos',
      pregunta: '¿Cuántos lotes necesito para el PQ? Me piden tres, pero no encuentro de dónde sale ese número.',
      quien: 'Óscar R.',
      instructor: 'Johan Rojas',
      iniciales: 'JR',
      respuesta: 'De ningún lado: tres es costumbre, no norma. El número sale del riesgo y de la variación que ya mediste. Si lo sustentas con datos, defiendes dos o justificas cinco. Eso es justo lo que revisa el auditor.',
    },
  },
];

export const FAQ_COMUN = FAQ;
export const INCLUYE_COMUN = INCLUYE;

export const porSlug = (slug) => CERTIFICACIONES.find((c) => c.slug === slug);

const fmt = (m) => `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;
const aprox = (m) => `${Math.round(m / 60)} h`;

/**
 * Duración de una certificación: video de sus módulos + minutos de ejercicio.
 * Vive aquí y no en la página para que el <title> y el <h1> no puedan discrepar.
 *
 * El minutaje de video SIEMPRE es exacto —sale de la ficha de cada curso—. El de
 * ejercicios depende de la certificación: Validación de Procesos lo trae del
 * plan de estudios y se publica al minuto; en Lean Leader y Green Belt es un
 * estimado nuestro, así que se redondea a horas y el total se queda en la cifra
 * que la certificación declara. Publicar "40 h 16 min" con números inventados
 * sería fingir una precisión que no tenemos.
 */
/**
 * Cuántos cursos tiene la ruta. Sale de `modulos`, nunca de un número escrito.
 * Existía como campo `cursos: 12` en las tres y al cambiar las listas —Lean
 * Leader a 11, Process Validation a 8— las tres siguieron diciendo doce.
 */
export const cursosDe = (cert) => cert.modulos.length;

export function duracionDe(cert) {
  const fichas = cert.modulos.map(([n]) => cursoPorNombre(n));
  if (!fichas.every(Boolean)) return { horas: cert.horas };
  const video = fichas.reduce((t, x) => t + x.minutos, 0);
  const ejercicios = cert.modulos.reduce((t, m) => t + (m[3] || 0), 0);
  if (!ejercicios) return { video: fmt(video), ejercicios: null, horas: cert.horas };
  const estimado = Boolean(cert.ejerciciosEstimados);
  const calculado = fmt(video + ejercicios);
  // `horas` es la cifra que se PUBLICA y sale de la ficha; `calculado` es la que
  // dan los datos. Se devuelven las DOS a propósito: la publicada suele ser un
  // número redondo de negocio —26 h, 40 h— y el cálculo es el que avisa cuando
  // se ha ido lejos.
  //
  // Antes `horas` se calculaba cuando los ejercicios eran reales, y por eso
  // Process Validation pasó de anunciar 40 h a 25 h 38 min sola al recortar la
  // ruta de 12 módulos a 8. Publicar es una decisión de negocio; el aviso de
  // abajo es lo que impide que la cifra publicada se separe en silencio.
  return {
    video: fmt(video),
    ejercicios: estimado ? aprox(ejercicios) : fmt(ejercicios),
    horas: cert.horas,
    calculado,
    estimado,
  };
}

// AVISO EN DESARROLLO. No cambia nada de lo que ve el visitante: solo grita en
// consola si la cifra publicada y la calculada se separan más de un 15%, que es
// lo que pasó cuando las listas de módulos cambiaron y las horas se quedaron
// como estaban. Un margen existe porque la publicada se redondea a propósito.
if (import.meta.env?.DEV) {
  const min = (t) => {
    const h = /(\d+)\s*h/.exec(t || ''); const m = /(\d+)\s*min/.exec(t || '');
    return (h ? +h[1] * 60 : 0) + (m ? +m[1] : 0);
  };
  for (const c of CERTIFICACIONES) {
    const d = duracionDe(c);
    if (!d.calculado) continue;
    const a = min(d.horas); const b = min(d.calculado);
    if (a && b && Math.abs(a - b) / b > 0.15) {
      console.warn(`[zenkai] ${c.nombre}: se publica ${d.horas} pero los datos dan ${d.calculado}. Revisa src/config/certificaciones.js`);
    }
  }
}
